import { Chip, CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import moment from 'moment';
import { useEffect } from 'react';

import WarningIcon from '../../../assets/svg/WarningIcon';
import { SubscriptionInvoice } from '../../../domain/subscription/SubscriptionInvoice';
import { useRetrieveSubscriptionInvoices } from '../../../hooks/subscription/useRetrieveSubscriptionInvoices';
import FormattedMessage from '../../../react-components/FormattedCustomMessage';
import { DataTable } from '../../../react-components/data-table/DataTable';

const styles = {
  root: {
    height: `100%`,
    width: `100%`,
    maxWidth: `800px`,
  },
  content: {
    width: `100%`,
    height: `100%`,
    display: `flex`,
    overflowX: `auto` as any,
    overflowY: `hidden` as any,
    flex: 1,
  },
  noPaymentDiv: {
    border: `1px solid ${RoutalPalette.warning}`,
    borderRadius: `8px`,
    backgroundColor: `rgba(248,231,28,0.1)`,
    padding: `8px`,
    height: `42px`,
  },
  billingsError: {
    display: `flex`,
    flexDirection: `row` as any,
    alignItems: `center`,
    border: `1px solid ${RoutalPalette.danger}`,
    borderRadius: `8px`,
    backgroundColor: `rgba(255, 40, 37, 0.1)`,
    padding: `8px`,
    height: `42px`,
  },
};

const StatusColors = {
  draft: `#e3e8ee`,
  open: `#cbf4c9`,
  paid: `#d6ecff`,
  uncollectible: `#fee3c0`,
  void: `#e6e6fc`,
};

type Props = {
  classes: {
    root: string;
    content: string;
    noPaymentDiv: string;
    billingsError: string;
  };
};

export const BillingInvoices = withStyles(styles)(({ classes }: Props) => {
  const {
    data: invoices,
    isLoading,
    error: hasError,
    run: getSubscriptionInvoices,
  } = useRetrieveSubscriptionInvoices();

  useEffect(() => {
    getSubscriptionInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{
        padding: `16px 22px`,
        backgroundColor: `white`,
        zIndex: 2,
        height: `100%`,
        overflowY: `auto`,
      }}
    >
      <Grid container direction="column" className={classes.root}>
        <Grid item>
          <Typography variant="h6">
            <FormattedMessage id="settings.billing.invoices.title" />
          </Typography>
        </Grid>

        <VSpacer />

        <Grid item className={classes.content}>
          {isLoading && (
            <Grid container direction="column" justifyContent="center">
              {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
              <Grid item align="center">
                <CircularProgress className="form-ignore" size={32} />
              </Grid>
              <VSpacer medium />
              {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
              <Grid item align="center">
                <FormattedMessage id="settings.billing.invoices.loading" />
              </Grid>
            </Grid>
          )}

          {!isLoading && (
            <>
              {hasError && (
                <div className={classes.billingsError}>
                  <WarningIcon style={{ float: `left`, marginRight: `16px`, color: RoutalPalette.danger }} />
                  <Typography style={{ float: `left` }}>
                    <FormattedMessage id="settings.billing.invoices.error" />
                  </Typography>
                </div>
              )}

              {!hasError && (invoices ?? []).length === 0 && (
                <Grid item container alignItems="center" className={classes.noPaymentDiv} sm={4}>
                  <WarningIcon style={{ float: `left`, marginRight: `16px`, color: RoutalPalette.warning }} />
                  <Typography style={{ float: `left` }}>
                    <FormattedMessage id="settings.billing.invoices.empty" />
                  </Typography>
                </Grid>
              )}

              {(invoices ?? []).length > 0 && (
                <DataTable
                  noFooter
                  docs={invoices!.map((invoice) => ({
                    doc: {
                      ...invoice.props,
                      id: `${invoice.id}`,
                    },
                    checkboxIcon: { element: <div style={{ width: `24px` }} /> },
                  }))}
                  showCheckboxes={false}
                  total={invoices!.length}
                  columns={[
                    {
                      width: 130,
                      accessorName: `date`,
                      align: `left`,
                      name: <FormattedMessage id="settings.billing.invoices.date" />,
                      formatter: ({ doc }: { doc: SubscriptionInvoice }) => {
                        return <span>{moment(doc.date).format(`YYYY/MM/DD hh:mm`)}</span>;
                      },
                    },
                    {
                      width: 220,
                      accessorName: `billNumber`,
                      align: `left`,
                      name: <FormattedMessage id="settings.billing.invoices.number" />,
                      formatter: ({ doc }: { doc: SubscriptionInvoice }) => {
                        return (
                          <Tooltip title={doc.bill_number}>
                            <Typography noWrap style={{ maxWidth: `220px` }}>
                              {doc.bill_number}
                            </Typography>
                          </Tooltip>
                        );
                      },
                    },
                    {
                      width: 80,
                      accessorName: `amount`,
                      align: `center`,
                      name: <FormattedMessage id="settings.billing.invoices.amount" />,
                      formatter: ({ doc }: { doc: SubscriptionInvoice }) => {
                        return <span>{(doc.amount ? doc.amount : 0).toFixed(2)}€</span>;
                      },
                    },
                    {
                      width: 120,
                      accessorName: `status`,
                      align: `center`,
                      name: <FormattedMessage id="settings.billing.invoices.status" />,
                      formatter: ({ doc }: { doc: SubscriptionInvoice }) => {
                        return (
                          <Chip
                            // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                            style={{ backgroundColor: StatusColors[doc.status] }}
                            label={<FormattedMessage id={`settings.billing.invoices.status.${doc.status}`} />}
                          />
                        );
                      },
                    },
                    {
                      width: 120,
                      accessorName: `id`,
                      align: `right`,
                      name: <FormattedMessage id="settings.billing.invoices.actions" />,
                      formatter: ({ doc }: { doc: SubscriptionInvoice }) => {
                        return (
                          <Grid container justifyContent="flex-end">
                            <a href={doc.invoice_pdf} target="_self" rel="noopener noreferrer">
                              <FormattedMessage id="settings.billing.invoices.actions.download" />
                            </a>
                          </Grid>
                        );
                      },
                    },
                  ]}
                />
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
});
